import serverVars from 'server-vars';
import { fetchQuery_DEPRECATED as fetchQuery, graphql, Environment } from 'react-relay/legacy';
import {
    recaptchaFeatureFlagQueryResponse,
    recaptchaFeatureFlagQuery,
} from './__generated__/recaptchaFeatureFlagQuery.graphql';
import widgetLoader from 'dibs-widget-loader';

import './styles/recaptcha.scss';

const NODE_RECAPTCHA_SITE_KEY_NAME = 'NODE_RECAPTCHA_SITE_KEY';
const NODE_RECAPTCHA_SITE_KEY_V3_NAME = 'NODE_RECAPTCHA_SITE_KEY_V3';

const sitekey = serverVars.get(NODE_RECAPTCHA_SITE_KEY_NAME);
const sitekeyv3 = serverVars.get(NODE_RECAPTCHA_SITE_KEY_V3_NAME);
const elId = `dibs-recaptcha`;

const query = graphql`
    query recaptchaFeatureFlagQuery(
        $featureFlag: String = ""
        $featureFlagV3: String = ""
        $hasFeatureFlag: Boolean!
        $hasFeatureFlagV3: Boolean!
    ) {
        viewer {
            featureFlag(feature: $featureFlag) @include(if: $hasFeatureFlag)
            v3Enabled: featureFlag(feature: $featureFlagV3) @include(if: $hasFeatureFlagV3)
        }
    }
`;

const isLoaded = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.grecaptcha !== 'undefined' &&
    typeof window.grecaptcha.render === 'function';

const isReady = new Promise<void>(resolve => {
    if (isLoaded()) {
        resolve();
    } else {
        const interval = setInterval(() => {
            if (isLoaded()) {
                clearInterval(interval);
                resolve();
            }
        }, 300);
    }
});

async function setUpCaptcha(): Promise<void> {
    if (document.getElementById(elId)) {
        return;
    }
    await widgetLoader.load('recaptcha');
    await isReady;

    // put callbacks on window or else functions get gc'd (i think?)
    window.dibsCaptchaCallbacks = { reject: () => {}, resolve: () => {} };

    const node = document.createElement('div');
    node.setAttribute('id', elId);
    node.setAttribute('data-sitekey', sitekey);
    document.body.appendChild(node);
    window.grecaptcha.render(node, {
        sitekey,
        size: 'invisible',
        callback: token => {
            window.dibsCaptchaCallbacks.resolve(token);
            window.grecaptcha.reset();
        },
        'error-callback': () => {
            window.dibsCaptchaCallbacks.reject();
            window.grecaptcha.reset();
        },
    });
}

async function recaptchaV3(): Promise<CaptchaResponse> {
    await widgetLoader.load('recaptchaEnterprise', { recaptchaV3SiteKey: sitekeyv3 });
    await new Promise<void>(resolve => {
        window.grecaptcha.enterprise.ready(() => resolve());
    });
    const captchaToken = await window.grecaptcha.enterprise.execute(sitekeyv3, {
        action: 'submit',
    });
    return { captchaToken, captchaKey: sitekeyv3 };
}

/**
 * Executes recaptcha,
 * relayEnvironment only needed if featureFlag is provided
 */
async function executeCaptcha(args?: {
    featureFlag?: string;
    featureFlagV3?: string;
    relayEnvironment?: Environment;
}): Promise<CaptchaResponse> {
    const { featureFlag, featureFlagV3, relayEnvironment } = args || {};
    if ((featureFlag || featureFlagV3) && relayEnvironment) {
        const result: recaptchaFeatureFlagQueryResponse = await fetchQuery<
            recaptchaFeatureFlagQuery
        >(relayEnvironment, query, {
            featureFlag,
            featureFlagV3,
            hasFeatureFlag: !!featureFlag,
            hasFeatureFlagV3: !!featureFlagV3,
        });
        // if a v3 flag is passed and it is enabled use v3 enterprise
        if (featureFlagV3 && result?.viewer?.v3Enabled) {
            return await recaptchaV3();
        }
        // else if a v3 flag is passed and it is not enabled return null
        if (featureFlag && !result?.viewer?.featureFlag) {
            return { captchaToken: null, captchaKey: null };
        }
    }
    // no flag is passed or v2 is and it is not enabled return null
    await setUpCaptcha();
    return await new Promise((resolve, reject) => {
        window.dibsCaptchaCallbacks.resolve = captchaToken =>
            resolve({ captchaToken, captchaKey: null });
        window.dibsCaptchaCallbacks.reject = reject;
        window.grecaptcha.execute();
    });
}

if (typeof window !== 'undefined') {
    // for easy testing from console
    window.recaptchaV3 = recaptchaV3;
    window.executeCaptcha = executeCaptcha;
}

export {
    NODE_RECAPTCHA_SITE_KEY_NAME,
    NODE_RECAPTCHA_SITE_KEY_V3_NAME,
    executeCaptcha,
    recaptchaV3,
};
